import {
  ConfiguratorItem,
  ConfiguratorText,
  IConfiguratorAnswers,
  IConfiguratorErrors,
  IConfiguratorQuestion,
  IConfiguratorSection,
  IConfiguratorSectionInstance,
  IConfiguratorStep
} from '../models/configurator.model';
import { getAnswer } from './configurator-answer-path.helper';
import { isEmptyAnswer } from './configurator-value.helper';

export { isEmptyAnswer, normalizeAnswer } from './configurator-value.helper';

export const DEFAULT_REQUIRED_MESSAGE = 'ProjectConfiguratorV2.Errors.required';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Translation key of a label, help or title that may depend on the answers. */
export const resolveText = (text: ConfiguratorText | undefined, answers: IConfiguratorAnswers): string =>
  typeof text === 'function' ? text(answers) : text || '';

export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value);

export const isSectionVisible = (section: IConfiguratorSection, answers: IConfiguratorAnswers): boolean =>
  section.show ? section.show(answers) : true;

export const isQuestionVisible = (
  question: IConfiguratorQuestion,
  answers: IConfiguratorAnswers,
  item: ConfiguratorItem = null
): boolean => (question.show ? question.show(answers, item) : true);

/** Answer path of a question: its id, or `<repeat key>.<index>.<id>` inside a repeated section. */
export const getQuestionPath = (question: IConfiguratorQuestion, instance?: IConfiguratorSectionInstance) =>
  instance?.pathPrefix ? `${instance.pathPrefix}.${question.id}` : question.id;

export const isQuestionAnswered = (
  question: IConfiguratorQuestion,
  answers: IConfiguratorAnswers,
  path: string = question.id
): boolean => {
  const value = getAnswer(answers, path);
  return question.answered ? question.answered(value, answers) : !isEmptyAnswer(value);
};

/**
 * Visible sections of a step. A repeated section (`repeat`) yields one instance per item, e.g. one per
 * bathroom; its questions read and write `<repeat key>.<index>.<question id>`.
 */
export const getSectionInstances = (
  step: IConfiguratorStep,
  answers: IConfiguratorAnswers
): IConfiguratorSectionInstance[] =>
  step.sections
    .filter((section) => isSectionVisible(section, answers))
    .reduce<IConfiguratorSectionInstance[]>((instances, section) => {
      if (!section.repeat) {
        return [...instances, { key: section.id, section, index: null, item: null, pathPrefix: '' }];
      }

      const items = (getAnswer(answers, section.repeat.key) as ConfiguratorItem[]) || [];
      const repeated = Array.from({ length: section.repeat.count(answers) }, (_, index) => ({
        key: `${section.id}-${index}`,
        section,
        index,
        item: items[index] || {},
        pathPrefix: `${section.repeat.key}.${index}`
      }));
      return [...instances, ...repeated];
    }, [])
    .filter((instance) =>
      instance.section.questions.some((question) => isQuestionVisible(question, answers, instance.item))
    );

/** Hidden questions keep their values but never block the step. Errors are keyed by answer path. */
export const validateStep = (step: IConfiguratorStep, answers: IConfiguratorAnswers): IConfiguratorErrors => {
  const errors: IConfiguratorErrors = {};

  getSectionInstances(step, answers).forEach((instance) =>
    instance.section.questions
      .filter((question) => isQuestionVisible(question, answers, instance.item))
      .forEach((question) => {
        const path = getQuestionPath(question, instance);
        if (!isQuestionAnswered(question, answers, path)) {
          if (question.required) errors[path] = question.requiredMessage || DEFAULT_REQUIRED_MESSAGE;
          return;
        }

        const error = question.validate?.(getAnswer(answers, path), answers);
        if (error) errors[path] = error;
      })
  );

  return errors;
};

export const emailFormatValidator =
  (message: string) =>
  (value: unknown): string | null =>
    typeof value === 'string' && !isValidEmail(value) ? message : null;
